import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { IntegrationTag } from '../../components/integration-components/integration-details-component/integration-tag'
import { InputBox } from '../../components/integration-components/integration-details-component/input-boxes'
import {
  useGetIntegrationDetails,
  useGetIntegrationStatus
} from '@/application/queries/use-integrations'
import { useSaveIntegrationCredentials } from '@/application/queries/use-integrations'
import { useEcommerceId } from '@/hooks/use-ecommerce-id'
import { Base, ChevronIcon, toast } from '@itsrever/design-system'
import { formatTag } from '@/ui/components/integration-components/integrationsUtils'
import { useAppAbout } from '@/ui/components/integration-components/use-app-about'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { AppstoreintegrationspbSaveIntegrationCredentialsRequestStatusEnum } from '@itsrever/rever-api'

const { Button } = Base

const isSecretField = (field: string) =>
  /password|secret|token|api_key/.test(field.toLowerCase())

// BRT sends no return depot unless the service needs one, and a blank tariff falls back to the contract's.
const OPTIONAL_FIELDS = new Set(['return_depot', 'pricing_condition_code'])

const isOptionalField = (field: string) =>
  OPTIONAL_FIELDS.has(field.toLowerCase())

const IntegrationDetails: React.FC = () => {
  const { t } = useTranslation()

  const ecommerceId = useEcommerceId()

  const { id } = useParams()
  const navigate = useNavigate()

  const { integrationDetails } = useGetIntegrationDetails(id)
  const { integrationStatusData } = useGetIntegrationStatus()
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  const { mutate: postCredentials, isPending } = useSaveIntegrationCredentials()
  const showDialog = useDialog()
  const about = useAppAbout()

  const [formData, setFormData] = useState<Record<string, string>>({})

  useEffect(() => {
    if (integrationDetails && integrationDetails.api_required_keys) {
      const formattedData: Record<string, string> = {}

      Object.keys(integrationDetails.api_required_keys).forEach((key) => {
        formattedData[key] = integrationDetails.api_required_keys?.[key] || ''
      })
      setFormData(formattedData)

      const integrationStatus = integrationStatusData?.find(
        (integ) => integ.name == integrationDetails.integration_name
      )

      if (integrationStatus) {
        setHasSubmitted(
          integrationStatus.status !== 'INTEGRATION_STATUS_INACTIVE'
        )
        setIsInstalled(integrationStatus.status === 'INTEGRATION_STATUS_ACTIVE')
      }
    }
  }, [integrationDetails, integrationStatusData])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!ecommerceId) {
      console.error('ecommerceId is required but missing.')
      return
    }

    if (
      hasSubmitted &&
      !(await showDialog({
        title: t('integrations_store.resubmit_credentials'),
        message: t('integrations_store.confirm_resubmit'),
        okButtonText: t('integrations_store.resubmit'),
        cancelButtonText: t('integrations_store.cancel')
      }))
    ) {
      return
    }
    submitCredentials()
  }

  const submitCredentials = () => {
    if (
      !integrationDetails?.integration_id ||
      !integrationDetails?.integration_name
    ) {
      console.error('Integration details are missing.')
      return
    }

    if (ecommerceId) {
      postCredentials(
        {
          integrationId: integrationDetails?.integration_id,
          integrationName: integrationDetails?.integration_name,
          ecommerceId,
          credentials: formData,
          // A resubmit too: INACTIVE (2) would ask REVER's team to switch the integration off.
          status:
            AppstoreintegrationspbSaveIntegrationCredentialsRequestStatusEnum.Active
        },
        {
          onSuccess: () => {
            setHasSubmitted(true)
            toast({
              variant: 'success',
              text: t('integrations_store.credentials_success')
            })
          },
          onError: () => {
            toast({
              variant: 'error',
              text: t('integrations_store.credentials_error')
            })
          }
        }
      )
    }
  }

  return (
    <main className="min-h-screen bg-neutral-100/10">
      <div className="min-h-full bg-neutral-100/10 px-5 pb-5 pt-4">
        <div className="flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 pb-4">
          <button
            className={clsx(
              'flex aspect-square size-8 items-center justify-center rounded-lg shadow-sm',
              'border border-solid border-[#E1E4EA]',
              'hover:shadow-sm hover:outline hover:outline-1 hover:outline-offset-0 hover:outline-[#E1E4EA]'
            )}
            onClick={() => navigate('/account/integrations')}
          >
            <ChevronIcon className="size-5 rotate-90 text-[#525866]" />
          </button>
          <header className="mr-auto space-y-1">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {integrationDetails?.integration_name}
            </h1>
            <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
              {t('integrations_store.integration_details')}
            </h2>
          </header>
        </div>
      </div>
      <div className="container px-5 py-8 pb-12">
        <Base.Card variant="flat" className="mx-auto flex w-fit gap-4 p-6">
          <div className="bg-whitemax-md:w-full border-r border-gray-300">
            <img
              src={integrationDetails?.integration_logo}
              alt={`${integrationDetails?.integration_name}`}
              className="mb-4 size-16 rounded-lg bg-[#F5F7FA] object-contain p-2"
              loading="lazy"
            />
            <h2
              style={{
                color: '#0E121B',
                fontSize: '32px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                lineHeight: '40px',
                wordWrap: 'break-word'
              }}
            >
              {integrationDetails?.integration_name}
            </h2>
            <p className="mb-2 max-w-96 text-sm text-gray-500">
              {about({
                appKey: integrationDetails?.integration_name ?? '',
                part: 'details',
                fallback: integrationDetails?.integration_description
              })}
            </p>

            {integrationDetails?.integration_category_tags &&
              integrationDetails?.integration_category_tags.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.57495 6.87527L10.5421 2.23052C10.6776 2.14007 10.837 2.0918 11 2.0918C11.1629 2.0918 11.3223 2.14007 11.4578 2.23052L18.425 6.87527V18.4253C18.425 18.6441 18.338 18.8539 18.1833 19.0086C18.0286 19.1634 17.8188 19.2503 17.6 19.2503H4.39995C4.18115 19.2503 3.97131 19.1634 3.81659 19.0086C3.66187 18.8539 3.57495 18.6441 3.57495 18.4253V6.87527ZM5.22495 7.75802V17.6003H16.775V7.75802L11 3.90857L5.22495 7.75802ZM11 10.1753C10.5623 10.1753 10.1427 10.0014 9.83323 9.692C9.52379 9.38256 9.34995 8.96288 9.34995 8.52527C9.34995 8.08766 9.52379 7.66798 9.83323 7.35854C10.1427 7.04911 10.5623 6.87527 11 6.87527C11.4376 6.87527 11.8572 7.04911 12.1667 7.35854C12.4761 7.66798 12.65 8.08766 12.65 8.52527C12.65 8.96288 12.4761 9.38256 12.1667 9.692C11.8572 10.0014 11.4376 10.1753 11 10.1753Z"
                        fill="#0E121B"
                      />
                    </svg>
                    <h3 className="text-base font-medium text-[#0E121B]">
                      {t('integrations_store.tags')}
                    </h3>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {integrationDetails?.integration_category_tags.map(
                      (tag, index) => {
                        return (
                          <IntegrationTag key={`bottom-${tag}-${index}`}>
                            {formatTag(tag)}
                          </IntegrationTag>
                        )
                      }
                    )}
                  </div>
                </div>
              )}
          </div>

          <Base.Card variant="muted" className="flex-1 p-6 max-md:w-full">
            <div className="flex items-center gap-2">
              <div data-svg-wrapper style={{ position: 'relative' }}>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.2504 13.9172L15.0838 12.749L16.2504 11.5824C16.6362 11.2 16.9426 10.7451 17.1522 10.244C17.3617 9.74278 17.4702 9.20514 17.4714 8.66192C17.4726 8.1187 17.3664 7.58059 17.1591 7.07849C16.9518 6.57639 16.6473 6.12019 16.2632 5.73607C15.8791 5.35195 15.4229 5.04749 14.9208 4.84016C14.4187 4.63282 13.8806 4.52671 13.3373 4.52789C12.7941 4.52908 12.2565 4.63756 11.7553 4.84709C11.2541 5.05662 10.7992 5.36308 10.4168 5.74887L9.25026 6.91625L8.08289 5.7497L9.25109 4.58315C10.3342 3.50007 11.8031 2.8916 13.3348 2.8916C14.8665 2.8916 16.3355 3.50007 17.4186 4.58315C18.5017 5.66622 19.1101 7.13519 19.1101 8.6669C19.1101 10.1986 18.5017 11.6676 17.4186 12.7506L16.2512 13.9172H16.2504ZM13.9173 16.2503L12.7499 17.4168C11.6668 18.4999 10.1979 19.1084 8.66616 19.1084C7.13446 19.1084 5.66549 18.4999 4.58241 17.4168C3.49934 16.3338 2.89087 14.8648 2.89087 13.3331C2.89087 11.8014 3.49934 10.3324 4.58241 9.24935L5.74979 8.0828L6.91634 9.251L5.74979 10.4175C5.36399 10.8 5.05753 11.2548 4.848 11.756C4.63847 12.2572 4.53 12.7949 4.52881 13.3381C4.52762 13.8813 4.63374 14.4194 4.84107 14.9215C5.04841 15.4236 5.35287 15.8798 5.73699 16.2639C6.1211 16.648 6.57731 16.9525 7.07941 17.1598C7.58151 17.3672 8.11961 17.4733 8.66283 17.4721C9.20606 17.4709 9.74369 17.3624 10.2449 17.1529C10.7461 16.9434 11.2009 16.6369 11.5834 16.2511L12.7499 15.0846L13.9173 16.2511V16.2503ZM13.3332 7.49952L14.5006 8.6669L8.66699 14.4996L7.49961 13.3331L13.3332 7.50035V7.49952Z"
                    fill="#0E121B"
                  />
                </svg>
              </div>
              <div className="text-lg font-medium text-[#0E121B]">
                {t('integrations_store.add_new_connection')}
              </div>
            </div>

            <p className="mb-6 text-sm text-gray-500">
              {t('integrations_store.fill_details', {
                integration: integrationDetails?.integration_name
              })}
            </p>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5 max-sm:gap-4"
            >
              {Object.keys(formData).length > 0 &&
                Object.keys(formData).map((field, index) => (
                  <InputBox
                    key={index}
                    id={field}
                    label={field}
                    labelSuffix={
                      isOptionalField(field)
                        ? t('integrations_store.optional_field')
                        : undefined
                    }
                    type={isSecretField(field) ? 'password' : 'text'}
                    required={!isOptionalField(field)}
                    value={formData[field] || ''}
                    onChange={(value) => handleInputChange(field, value)}
                  />
                ))}
              <Button loading={isPending} type="submit" variant="primary">
                {isInstalled
                  ? 'Resubmit credentials'
                  : Object.keys(formData).length > 0
                    ? 'Submit credentials'
                    : 'Request connection'}
              </Button>
            </form>
          </Base.Card>
        </Base.Card>
      </div>
    </main>
  )
}

export default IntegrationDetails
